import Vue from 'vue'

import { buildOrdersQueryString } from '@/helpers/ordersQueryParams'
import { fetchDivisions } from '@/services/divisionsService'
import { createBoundedTtlSet } from '@/utils/boundedTtlSet'
import { $themeConfig } from '@themeConfig'

import { ordersStore } from '../gql/query'

import { classifyUserOrderEvent, USER_ORDER_EVENT_KINDS } from './validators'

const PROCESSED_EVENT_TTL = 30 * 60 * 1000
const PROCESSED_EVENT_LIMIT = 5000
const processedEventIds = createBoundedTtlSet({
    ttlMs: PROCESSED_EVENT_TTL,
    maxSize: PROCESSED_EVENT_LIMIT,
})
let processedEventsOwner = null

const {
    GET_STATUSES,
    GET_ORDER_LOG_BY_ID,
    GET_OPERATORS,
    GET_COOKERY_INTERVALS,
} = ordersStore()

export default {
    handleUserOrderEvent({ commit }, data) {
        let descriptor
        try {
            descriptor = classifyUserOrderEvent(data)
        } catch (error) {
            console.warn(error.message)
            return true
        }

        if (!descriptor) return false
        if (descriptor.eventId && processedEventIds.has(descriptor.eventId)) {
            return true
        }

        const mutation = {
            [USER_ORDER_EVENT_KINDS.KICK_REQUESTED]: 'SET_UNBLOCKED_ORDER',
            [USER_ORDER_EVENT_KINDS.KICK_CONFIRMED]:
                'SET_CONFIRMED_UNBLOCKED_ORDER_ID',
            [USER_ORDER_EVENT_KINDS.LEGACY_CLOSED]:
                'SET_CANCELED_ORDER_NOTIFICATION',
            [USER_ORDER_EVENT_KINDS.LEGACY_UPDATED]:
                'SET_CHANGED_ORDER_NOTIFICATION',
        }[descriptor.kind]
        const payload =
            descriptor.kind === USER_ORDER_EVENT_KINDS.KICK_CONFIRMED
                ? descriptor.orderId
                : descriptor.payload

        commit(mutation, payload)
        if (descriptor.eventId) processedEventIds.add(descriptor.eventId)
        return true
    },

    setUserOrderEventsOwner(ctx, login) {
        if (processedEventsOwner === login) return
        processedEventIds.clear()
        processedEventsOwner = login
    },

    resetUserOrderEvents() {
        processedEventIds.clear()
        processedEventsOwner = null
    },

    async fetchOrders(
        { state },
        { limit, offset, sortBy, orderBy, search = '' }
    ) {
        const clearSearch = !search
            ? ''
            : search.trim().replace(/\+|\s|\(|\)/g, '')
        const param =
            clearSearch && clearSearch.length > 12 ? 'transaction_id' : 'phone'
        const query = !clearSearch
            ? ''
            : `&${param}=${
                  param === 'phone' ? clearSearch.replace('-', '') : clearSearch
              }`

        const sorting = sortBy
            ? `&sort_by=${sortBy}&order_by=${orderBy || 'DESC'}`
            : ''

        const getStr = `${
            $themeConfig.app.backUrl
        }/orders?limit=${limit}&offset=${offset}${buildOrdersQueryString(
            state.paramsSearch
        )}${query}${sorting}`

        try {
            return await Vue.axios.get(getStr)
        } catch (error) {
            console.error(error)
            throw error
        }
    },

    fetchStatuses({ state, commit }) {
        return new Promise((resolve, reject) => {
            if (state.statuses.length === 0) {
                Vue.axios
                    .post($themeConfig.app.gqlUrl, {
                        query: GET_STATUSES,
                        variables: {},
                    })
                    .then(response => {
                        commit('ADD_TO_STATUSES', response.data)
                        resolve()
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },

    fetchErrorOrdersData({ commit }) {
        Vue.axios
            .get(`${$themeConfig.app.backUrl}/statuses/count?`, {
                params: {
                    today: true,
                    statuses: 'send_error',
                },
            })
            .then(response => {
                commit('SET_ERROR_ORDERS_DATA', response.data)
            })
            .catch(error => {
                console.error(error)
            })
    },

    async fetchRestaurants({ state, commit }) {
        if (state.restaurants.length) return

        try {
            const response = await fetchDivisions({ mod: 'short' })
            commit('ADD_TO_RESTAURANTS', response.data)
        } catch (error) {
            console.error('Ошибка загрузки ресторанов', error)
            throw error
        }
    },

    fetchOperators({ state, commit }, org) {
        return new Promise((resolve, reject) => {
            if (state.operators.length === 0) {
                Vue.axios
                    .post($themeConfig.app.gqlUrl, {
                        query: GET_OPERATORS,
                        variables: {
                            org,
                        },
                    })
                    .then(response => {
                        commit('ADD_TO_OPERATORS', response.data)
                        resolve()
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },

    fetchBrands({ state, commit }) {
        return new Promise((resolve, reject) => {
            if (state.brands.length === 0) {
                Vue.axios
                    .get(`${$themeConfig.app.backUrlDivisions}/brands`)
                    .then(response => {
                        commit('SET_BRANDS', response.data)
                        resolve()
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },

    fetchOrderById({ commit }, id) {
        return new Promise((resolve, reject) => {
            Vue.axios
                .get(`${$themeConfig.app.backUrl}/orders/${id}`)
                .then(response => {
                    commit('SET_PREVIEW_ORDER', response)
                    resolve()
                })
                .catch(error => {
                    reject(error)
                })
        })
    },

    fetchLogsByIdOrder(ctx, id) {
        return new Promise((resolve, reject) => {
            if (id) {
                Vue.axios
                    .post(`${$themeConfig.app.gqlUrl}?refresh=true`, {
                        query: GET_ORDER_LOG_BY_ID,
                        variables: { orderId: id },
                    })
                    .then(response => {
                        resolve(response.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },

    fetchChangesByIdOrder(ctx, { id, limit, offset }) {
        const getStr = `${$themeConfig.app.backUrl}/changes?order_id=${id}&limit=${limit}&offset=${offset}`
        return new Promise((resolve, reject) => {
            if (id) {
                Vue.axios
                    .get(getStr)
                    .then(response => {
                        resolve(response.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },
    fetchStatistics(ctx, { sd, sm, login }) {
        return new Promise((resolve, reject) => {
            const config = {
                login,
                start_date: sd || sm,
            }
            Vue.axios
                .get(`${$themeConfig.app.backUrl}/orders/statistics`, {
                    params: config,
                })
                .then(response => {
                    resolve(response.data)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },
    fetchNotifications({ commit }, id) {
        return new Promise((resolve, reject) => {
            if (id) {
                Vue.axios
                    .get(
                        `${$themeConfig.app.backUrl}/orders/${id}/notification_events`
                    )
                    .then(response => {
                        if (response.data)
                            commit('SET_NOTIFICATIONS', response.data)
                        else commit('SET_NOTIFICATIONS', null)
                        resolve()
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },
    fetchCookeryIntervals({ state, commit }) {
        return new Promise((resolve, reject) => {
            if (!state.cookeryIntervals?.length) {
                Vue.axios
                    .post($themeConfig.app.gqlUrl, {
                        query: GET_COOKERY_INTERVALS,
                    })
                    .then(response => {
                        commit(
                            'SET_COOKERY_INTERVALS',
                            response.data?.data?.cookery_interval
                        )
                        resolve()
                    })
                    .catch(error => {
                        reject(error)
                    })
            } else {
                resolve()
            }
        })
    },
}
